import type {Block} from './types';

export interface StreetBlockGeometry { block: Block; start: number; end: number; width: number }
export interface StreetGeometry {
  blocks: readonly StreetBlockGeometry[];
  byId: ReadonlyMap<number, StreetBlockGeometry>;
  totalWidth: number;
  blockAt(x: number): StreetBlockGeometry | undefined;
}

/** Metadata is complete even when artwork is viewport-paged. Never infer an
 * absent predecessor's width: that would silently move every later panel. */
export function buildStreetGeometry(blocks: readonly Block[], resolveWidth: (block:Block)=>number): StreetGeometry {
  const ordered = [...blocks].sort((a,b)=>a.id-b.id);
  const spans: StreetBlockGeometry[] = [];
  let start = 0;
  for (const [id,block] of ordered.entries()) {
    if (block.id!==id) throw new Error('Street geometry requires every opened block in order.');
    const width = resolveWidth(block);
    if (!Number.isSafeInteger(width) || width<=0) throw new Error('Invalid street block width.');
    spans.push({block,start,end:start+width,width});
    start += width;
    if (!Number.isSafeInteger(start)) throw new Error('Street geometry exceeds supported coordinates.');
  }
  const byId = new Map(spans.map(span=>[span.block.id,span]));
  return {blocks:spans,byId,totalWidth:start,blockAt(x) {
    if (!Number.isFinite(x)||!spans.length) return;
    if (x<0) return spans[0];
    if (x>=start) return spans.at(-1);
    let lo=0,hi=spans.length-1;
    while(lo<hi) {const mid=Math.floor((lo+hi)/2);if(x<spans[mid].end)hi=mid;else lo=mid+1;}
    return spans[lo];
  }};
}
